export type StepUpMode = 'totp' | 'email'

// A step-up code is always required; `totpEnabled` only picks which kind — an
// authenticator/backup code when 2FA is on, otherwise an emailed code. Mirrors
// the backend `step_up_gate`. `hasPassword` is orthogonal (it gates the password
// field, not the code kind), so it is deliberately not an input here.
export function getStepUpMode(totpEnabled: boolean): StepUpMode {
  return totpEnabled ? 'totp' : 'email'
}

export interface StepUpVerification {
  totpCode?: string
  backupCode?: string
  emailCode?: string
}

// The backend `verification` oneof carries exactly one code. Forward whichever
// the user filled; a TOTP code wins over a backup code if both are present.
export function pickStepUpVerification(values: StepUpVerification): StepUpVerification {
  if (values.totpCode) return { totpCode: values.totpCode }
  if (values.backupCode) return { backupCode: values.backupCode }
  if (values.emailCode) return { emailCode: values.emailCode }
  return {}
}

/** Where a refused step-up-gated request lands in its dialog, keyed on the server's
 *  error_message prefix. `conflict` is the billing settings write race (ENG-1094). */
export type StepUpFailure =
  | { kind: 'password' }
  | { kind: 'totp-required' }
  | { kind: 'email-code-required' }
  | { kind: 'totp-invalid' }
  | { kind: 'email-code-invalid' }
  | { kind: 'conflict' }
  | { kind: 'other'; message: string }

export function classifyStepUpError(message: string | undefined): StepUpFailure {
  const text = message?.trim() ?? ''
  if (text.startsWith('Invalid current password')) return { kind: 'password' }
  if (text.startsWith('TOTP_REQUIRED')) return { kind: 'totp-required' }
  if (text.startsWith('EMAIL_CODE_REQUIRED')) return { kind: 'email-code-required' }
  if (text.startsWith('TOTP_INVALID')) return { kind: 'totp-invalid' }
  if (text.startsWith('EMAIL_CODE_INVALID')) return { kind: 'email-code-invalid' }
  if (text.startsWith('Your billing settings changed')) return { kind: 'conflict' }
  return { kind: 'other', message: text }
}
